import {
  CreateProductGroupPermissionsResult,
  CreateProductGroupPermissionsVariables,
  createProductGroupPermissionsMutation,
} from "../../graphqlQueries/createProductGroupPermissions";
import {
  DeleteProductGroupResult,
  DeleteProductGroupVariables,
  deleteProductGroupMutation,
} from "../../graphqlQueries/deleteProductGroup";
import {
  GetProductGroupDataResult,
  GetProductGroupDataVariables,
  getProductGroupDataQuery,
} from "../../graphqlQueries/productGroup/getProductGroupData";
import {
  GetProductGroupsResult,
  getProductGroupsQuery,
} from "../../graphqlQueries/productGroup/getProductGroups";
import GroupForm, {
  ProductGroupFormValues,
  ProductGroupParticipantFormValue,
  company,
  user,
} from "./GroupForm";
import {
  LockProductGroupResult,
  LockProductGroupVariables,
  lockProductGroupMutation,
} from "../../graphqlQueries/lockProductGroup";
import { MutationQueryReducersMap, useApolloClient, useQuery } from "@apollo/client";
import {
  EditProductInformation,
  ProductGroupImage,
  ProductGroupProductInformation,
  UpdateProductGroupResult,
  UpdateProductGroupVariables,
  updateProductGroupMutation,
} from "../../graphqlQueries/productGroup/updateProductGroup";
import {
  AlertProps,
  DataHubImage,
  ProductGroupProductInput,
  ProductGroupRole,
  UserRole,
} from "../../types";
import {
  UnLockProductGroupResult,
  UnLockProductGroupVariables,
  unLockProductGroupMutation,
} from "../../graphqlQueries/unLockProductGroup";
import {
  UpdateProductGroupProductsResult,
  UpdateProductGroupProductsVariables,
  updateProductGroupProductsMutation,
} from "../../graphqlQueries/productGroup/updateProductGroupProducts";
import {
  removeProductGroupPermissionsMutation,
  removeProductGroupPermissionsResult,
  removeProductGroupPermissionsVariables,
} from "../../graphqlQueries/removeProductGroupPermissions";
import {
  UpdateProductGroupPermissionByPkResult,
  UpdateProductGroupPermissionByPkVariables,
  updateProductGroupPermissionByPkMutation,
} from "../../graphqlQueries/updateProductGroupPermissionByPk";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import { EDIT_PRODUCT_GROUP } from "./productGroupRouteConstants";
import { HasuradbProduct } from "../../graphqlQueries/getProduct";
import Loading from "../Loading";
import React, { useState } from "react";
import { getHasuraRoleContext } from "../../utils/functions";
import { getOperationName } from "@apollo/client/utilities";
import {
  getEditPath,
  getProductGroupRole,
  getProductGroupVariables,
} from "../../utils/productGroupUtils";
import { useTranslation } from "react-i18next";
import { userInfoVar } from "../../utils/ApolloCache";
import {
  GetProductGroupPermissionsResult,
  GetProductGroupPermissionsVariables,
  getProductGroupPermissionsQuery,
} from "../../graphqlQueries/productGroup/getProductGroupPermissions";

type EditGroupFormProps = {
  rootPagePath: string;
};

export type GroupIdParam = {
  groupId: string;
};

const scrollToNotification = () => {
  const notification = document.getElementById("locked-group-notification-alert");
  notification?.scrollIntoView({ behavior: "smooth" });
};

const getProductGroupsQueryUpdater = ():
  | MutationQueryReducersMap<DeleteProductGroupResult>
  | undefined => {
  const queryName = getOperationName(getProductGroupsQuery);
  if (!queryName) {
    return undefined;
  }
  return {
    [queryName]: (prev, options) => {
      const oldResult = prev as GetProductGroupsResult;
      const deletedGroupId = options.mutationResult.data?.updateProductGroupByPk.id;
      if (!deletedGroupId) {
        return oldResult;
      }
      const newResult: GetProductGroupsResult = {
        productGroup: oldResult.productGroup.filter((group) => group.id !== deletedGroupId),
      };
      return newResult;
    },
  };
};

const EditGroupForm: React.FunctionComponent<EditGroupFormProps> = ({ rootPagePath }) => {
  const history = useHistory();
  const apolloClient = useApolloClient();
  const { t } = useTranslation();
  const location = useLocation<{ alert: AlertProps } | undefined>();
  const [alert, setAlert] = useState<AlertProps | undefined>(location.state?.alert);
  const editParams = useRouteMatch<GroupIdParam>(`${rootPagePath}${EDIT_PRODUCT_GROUP}`)?.params;
  const groupId = editParams?.groupId ?? "";
  const currentUserId = userInfoVar()?.userId;
  const currentCompanyIds = userInfoVar()?.companyIds;

  const { data: userRoles } = useQuery<
    GetProductGroupPermissionsResult,
    GetProductGroupPermissionsVariables
  >(getProductGroupPermissionsQuery, {
    context: getHasuraRoleContext(UserRole.ManageProductGroup),
    fetchPolicy: "network-only",
    variables: { groupId },
  });

  const currentUserRole = getProductGroupRole(
    userRoles?.productGroupByPk,
    currentUserId,
    currentCompanyIds
  );
  const canManage =
    currentUserRole === ProductGroupRole.Admin || currentUserRole === ProductGroupRole.Manager;

  const { data: groupData, loading: groupDataLoading, error: groupDataError } = useQuery<
    GetProductGroupDataResult,
    GetProductGroupDataVariables
  >(getProductGroupDataQuery(canManage), {
    context: getHasuraRoleContext(UserRole.ManageProductGroup),
    fetchPolicy: "network-only",
    variables: { groupId },
  });

  const editedProductDetailsFromDb = groupData?.productGroupByPk?.products
    .map(({ product }: { product: HasuradbProduct }) => {
      return product?.productGroupProductInformations;
    })
    ?.map((information) => {
      return information?.map((info) => info);
    })
    .flat();

  const editedProductImagesFromDb = groupData?.productGroupByPk?.products
    .map(({ product }: { product: HasuradbProduct }) => {
      return product?.productGroupProductImages;
    })
    ?.map((image) => {
      return image?.map((img) => img);
    })
    .flat();

  if (groupDataLoading || !currentUserRole) {
    return <Loading />;
  }

  if (!groupData || groupDataError) {
    return <p>{t("common.error")}</p>;
  }

  const displayAlert = (err: Error) => {
    console.error(err);
    setAlert({ text: err.message, type: "danger" });
  };

  const onLock = async () => {
    try {
      await apolloClient.mutate<LockProductGroupResult, LockProductGroupVariables>({
        context: getHasuraRoleContext(UserRole.ManageProductGroup),
        mutation: lockProductGroupMutation,
        variables: {
          id: groupId,
        },
      });
      scrollToNotification();
    } catch (err) {
      const error = err as Error;
      displayAlert(error);
    }
  };

  const onUnLock = async () => {
    try {
      await apolloClient.mutate<UnLockProductGroupResult, UnLockProductGroupVariables>({
        context: getHasuraRoleContext(UserRole.ManageProductGroup),
        mutation: unLockProductGroupMutation,
        variables: {
          id: groupId,
        },
      });
      scrollToNotification();
    } catch (err) {
      const error = err as Error;
      displayAlert(error);
    }
  };

  const onSubmit = async (
    formValues: ProductGroupFormValues & {
      currentOrganizations: ProductGroupParticipantFormValue[];
      currentUsers: ProductGroupParticipantFormValue[];
    },
    products: ProductGroupProductInput[],
    editedProductDetails?: ProductGroupProductInformation[],
    editedProductImages?: ProductGroupImage[]
  ) => {
    const {
      currentOrganizations,
      currentUsers,
      usersFromDb: originalUsers,
      organizationsFromDb: originalOrganizations,
      emailsToInvite,
      organizationsToInvite,
      rolesToChange,
    } = formValues;

    const variables: UpdateProductGroupVariables = getProductGroupVariables({
      editedProductDetails,
      editedProductDetailsFromDb,
      editedProductImages,
      editedProductImagesFromDb,
      groupId,
      productGroup: {
        description: formValues.groupInstructions,
        name: formValues.groupName,
      },
      products,
    });

    const addedProductImages = editedProductImages?.filter((newPicture) => {
      if (!editedProductImagesFromDb || editedProductImagesFromDb.length === 0) {
        return editedProductImages;
      }
      return !editedProductImagesFromDb.some(
        (dbPicture) => dbPicture?.filename === newPicture?.filename
      );
    });
    const newInformation = editedProductDetailsFromDb?.filter((edited) =>
      editedProductDetails?.some((p) => p.productId === edited?.productId)
    );
    const hasEditedProductImages =
      editedProductImages &&
      editedProductImages?.length > 0 &&
      editedProductImagesFromDb &&
      editedProductImagesFromDb?.length <= editedProductImages.length;

    const hasAddedProductImages = addedProductImages?.length !== 0;
    const hasAddedInformation = newInformation?.length === 0;
    const hasRemovedProductImages =
      editedProductImagesFromDb && editedProductImages
        ? editedProductImagesFromDb.length > editedProductImages.length
        : false;

    const removedProductImages = editedProductImagesFromDb?.filter((i) =>
      editedProductImages?.find((r) => r.filename !== i?.filename)
    );

    const editImages =
      addedProductImages && addedProductImages.length > 0
        ? editedProductImages?.filter(
            (image) =>
              !addedProductImages.some((addedImage) => addedImage?.filename === image?.filename)
          )
        : editedProductImages || [];

    try {
      if (canManage) {
        const oldProducts = groupData.productGroupByPk.products;
        const newProducts = products.filter(
          (p) => !oldProducts.find((gp) => gp.product.id === p.productId)
        );
        const removedProducts = oldProducts
          .filter((gp) => !products.find((p) => gp.product.id === p.productId))
          .map((dp) => ({ productId: dp.product.id }));

        await apolloClient.mutate<UpdateProductGroupResult, UpdateProductGroupVariables>({
          context: getHasuraRoleContext(UserRole.ManageProductGroup),
          mutation: updateProductGroupMutation(
            hasAddedProductImages,
            addedProductImages,
            editedProductDetailsFromDb,
            editedProductDetails as EditProductInformation[],
            editedProductImagesFromDb,
            editImages,
            removedProducts,
            hasAddedInformation,
            hasEditedProductImages,
            hasRemovedProductImages,
            removedProductImages as DataHubImage[]
          ),
          refetchQueries: [getProductGroupsQuery, getProductGroupDataQuery(canManage)],
          variables: {
            ...variables,
            addedProductImages: addedProductImages || [],
            editImages: editImages || [],
            editedProductImages: editedProductImages || [],
            editedProductInformationIds: [...products].map((p) => p.productId),
            newProducts,
          },
        });
      } else if (currentUserRole === ProductGroupRole.Editor) {
        const oldProducts = groupData.productGroupByPk.products;

        const addedProducts = products.filter(
          (p) => !oldProducts.find((gp) => gp.product.id === p.productId)
        );

        const removedProducts = oldProducts
          .filter((gp) => !products.find((p) => gp.product.id === p.productId))
          .map((dp) => ({ productId: dp.product.id }));

        const hasRemovedProductImages =
          editedProductImagesFromDb && editedProductImages
            ? editedProductImagesFromDb.length > editedProductImages.length
            : false;

        await apolloClient.mutate<
          UpdateProductGroupProductsResult,
          UpdateProductGroupProductsVariables
        >({
          context: getHasuraRoleContext(UserRole.ManageProductGroup),
          mutation: updateProductGroupProductsMutation(
            hasEditedProductImages,
            hasRemovedProductImages,
            editedProductImages,
            hasAddedInformation,
            editedProductDetails as EditProductInformation[],
            hasAddedProductImages,
            addedProductImages,
            removedProductImages as DataHubImage[]
          ),
          refetchQueries: [getProductGroupsQuery, getProductGroupDataQuery(canManage)],
          variables: {
            ...variables,
            addedProducts,
            editedProductIds: [...addedProducts, ...removedProducts].map((p) => p.productId),
          },
        });
      } else {
        displayAlert(new Error("User does not have priviledges to update product group"));
        return;
      }
    } catch (err) {
      const error = err as Error;
      displayAlert(error);
      return;
    }

    try {
      // Only admin and manager can edit users and orgnizations
      if (canManage) {
        const usersToRemove = originalUsers?.filter(
          (user) => !currentUsers.find((currentUser) => currentUser.id === user.id)
        );
        const organizationsToRemove = originalOrganizations?.filter(
          (organization) =>
            !currentOrganizations.find(
              (currentOrganization) => currentOrganization.id === organization.id
            )
        );

        const newOrganizations = organizationsToInvite?.map((organization) => ({
          companyId: organization.value,
          role: ProductGroupRole.Editor,
        }));

        const newUsers = emailsToInvite?.map((user) => ({
          role: ProductGroupRole.Editor,
          userId: user.value,
        }));

        // If there are new organizations or users to be added, add them
        if (
          (newOrganizations && newOrganizations.length > 0) ||
          (newUsers && newUsers.length > 0)
        ) {
          await apolloClient.mutate<
            CreateProductGroupPermissionsResult,
            CreateProductGroupPermissionsVariables
          >({
            context: getHasuraRoleContext(UserRole.ManageProductGroup),
            mutation: createProductGroupPermissionsMutation,
            refetchQueries: [getProductGroupDataQuery(canManage)],
            variables: {
              companies: newOrganizations,
              productGroupId: groupId,
              users: newUsers,
            },
          });
        }
        // If there are users or organizations to be removed, remove them
        if (
          (usersToRemove && usersToRemove.length > 0) ||
          (organizationsToRemove && organizationsToRemove.length > 0)
        ) {
          await apolloClient.mutate<
            removeProductGroupPermissionsResult,
            removeProductGroupPermissionsVariables
          >({
            context: getHasuraRoleContext(UserRole.ManageProductGroup),
            mutation: removeProductGroupPermissionsMutation,
            refetchQueries: [getProductGroupDataQuery(canManage)],
            variables: {
              permissionRowIds: [
                ...(usersToRemove?.map((user) => user.id) || []),
                ...(organizationsToRemove?.map((organization) => organization.id) || []),
              ],
              productGroupId: groupId,
            },
          });
        }

        // If there are roles to be changed, change them
        if (rolesToChange) {
          const toUpdate = (
            e: Record<string, { label: string; value: string } | undefined> = {},
            original: ProductGroupParticipantFormValue[] | undefined = [],
            isUser?: boolean
          ) =>
            Object.entries(e)
              .filter(([k, v]) => original?.find((o) => o.value === k)?.role !== v?.value)
              .map(([k, v]) => ({
                id:
                  groupData.productGroupByPk.permissions.find((p) =>
                    isUser ? p.user?.id === k : p.company?.id === k
                  )?.id ?? "",
                role: v?.value ?? "",
              }));

          const roles = toUpdate(rolesToChange?.company, originalOrganizations).concat(
            toUpdate(rolesToChange?.user, originalUsers, true)
          );

          const updateRole = async ({ id, role = "" }: { id: string; role?: string }) =>
            await apolloClient.mutate<
              UpdateProductGroupPermissionByPkResult,
              UpdateProductGroupPermissionByPkVariables
            >({
              context: getHasuraRoleContext(UserRole.ManageProductGroup),
              mutation: updateProductGroupPermissionByPkMutation,
              refetchQueries: [getProductGroupPermissionsQuery],
              variables: { groupId, id, role },
            });

          await Promise.all(roles.map((role) => updateRole(role)));
        }
      }
      history.replace(getEditPath(rootPagePath, groupId));
      setAlert({ text: t("common.success"), type: "success" });
    } catch (err) {
      const error = err as Error;
      displayAlert(error);
    }
  };

  const onRemove = async () => {
    const variables = {
      id: groupId,
    };
    try {
      await apolloClient.mutate<DeleteProductGroupResult, DeleteProductGroupVariables>({
        context: getHasuraRoleContext(UserRole.ManageProductGroup),
        mutation: deleteProductGroupMutation,
        updateQueries: getProductGroupsQueryUpdater(),
        variables,
      });
      history.push(rootPagePath);
    } catch (err) {
      const error = err as Error;
      displayAlert(error);
    }
  };

  return (
    <GroupForm
      rootPagePath={rootPagePath}
      groupData={groupData.productGroupByPk}
      organizationsFromDb={groupData?.productGroupByPk?.permissions
        .filter((permission) => permission.company)
        .map((permission) => company({ permission }))}
      usersFromDb={groupData?.productGroupByPk?.permissions
        .filter((permission) => permission.user)
        .map((permission) => user({ permission }))}
      onSubmit={onSubmit}
      onRemove={onRemove}
      onLock={onLock}
      onUnLock={onUnLock}
      alert={alert}
      closeAlert={() => setAlert(undefined)}
      productGroupRole={currentUserRole}
      productsFromDb={groupData?.productGroupByPk.products}
    />
  );
};

export default EditGroupForm;
